/**
 * Layout sidebar — collapsible nav group.
 *
 * Extracted from `Layout.jsx` (no behaviour change). Renders one group
 * from `NAV_GROUPS` with its visible items (filtered via
 * `visibleItems(...)`). State (open/closed) persists in localStorage,
 * and Owner/Admin default-open or any group containing the active
 * route opens automatically.
 */
import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { visibleItems } from "@/components/layout/navVisibility";
import ServicesTreeNode from "@/components/layout/ServicesTreeNode";

export default function SidebarGroup({ group, user, sub, pathname, onNavigate, canHR }) {
  const visible = visibleItems(group.items, user, sub, canHR);
  const hasActive = visible.some((it) => pathname === it.to || pathname.startsWith(it.to + "/"));
  const STORAGE_KEY = `nav.group.${group.key}`;
  const persisted = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
  // 2026-08-25 · Default state = COLLAPSED for every user.
  // The historical Owner/Admin auto-expand rule opened every nav
  // group on first login which the user found noisy on the Home
  // screen. Now every group starts collapsed unless (a) the user
  // has previously toggled it open (persisted in localStorage) or
  // (b) the group contains the currently-active route (in which
  // case we auto-expand to reveal where the user is). The active-
  // route auto-open runs both on mount (via defaultOpen below) and
  // on route changes (via the useEffect below).
  const defaultOpen = hasActive;
  const [open, setOpen] = useState(persisted == null ? defaultOpen : persisted === "1");
  useEffect(() => { if (hasActive && !open) setOpen(true); /* eslint-disable-next-line */ }, [pathname]);

  if (visible.length === 0) return null;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { window.localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch (e) { /* ignore */ }
  };

  return (
    <div className="mb-1" data-testid={`nav-group-${group.key}`}>
      <button onClick={toggle} type="button"
        title={group.label}
        className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-md text-[10px] uppercase tracking-widest font-bold text-slate-400 hover:text-slate-200`}
        data-testid={`nav-group-toggle-${group.key}`} aria-expanded={open}>
        <span className="truncate">{group.label}</span>
        <ChevronRight className={`w-3 h-3 transition-transform shrink-0 ${open ? "rotate-90" : ""}`} />
      </button>
      {open ? (
        <div className="mt-0.5 space-y-0.5 pl-1.5">
          {visible.map((it) => {
            if (it.kind === "tree" && it.to === "/cases") {
              return <ServicesTreeNode key={it.to} item={it} pathname={pathname} onNavigate={onNavigate} />;
            }
            return (
              <NavLink key={it.to} to={it.to} end={it.to === "/"} data-testid={it.testid}
                       onClick={onNavigate}
                       title={it.label}
                       className={({ isActive }) => `co-sidebar-link co-sidebar-child ${isActive ? "active" : ""}`}>
                <it.icon className="w-4 h-4 shrink-0" />
                <span className="truncate">{it.label}</span>
              </NavLink>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
